import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Upload, ClipboardList, Layers,
  Presentation, CheckCircle2, AlertOctagon, XCircle, GitCompareArrows,
  BookOpen, HelpCircle, ClipboardCheck, Building2 } from "lucide-react";

import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import Reveal from "@/components/site/Reveal";
import { TESTIDS } from "@/lib/testIds";
import { setPathway, withPathway } from "@/lib/pathway";

/**
 * /companies — public pathway page.
 *
 * ImpactVest for companies preparing for fundraising, investor
 * diligence, sustainability-related scrutiny or value-chain requests.
 *
 * Signature output: Investor Evidence Pack.
 * Journey: Submit → Substantiate → Strengthen → Present.
 *
 * Locked framing (per P1_PATHWAYS_ARCHITECTURE.md):
 * - NEVER "prove your ESG impact".
 * - NEVER "certified", "assured", "verified", "guaranteed".
 * - Framework mapping is evidence organisation, NOT compliance.
 * - Suggested claim revisions are always presented; never auto-applied.
 */

const JOURNEY = [
  { n: "01", label: "Submit", icon: Upload,
    body: "Upload the pitch deck, impact report, KPI files, policies, methodology notes and supporting evidence. ImpactVest classifies every document and extracts every material claim." },
  { n: "02", label: "Substantiate", icon: ClipboardList,
    body: "Every claim is mapped to its underlying evidence. KPIs are checked for definition, baseline, boundary, methodology, reporting period and owner — no evidence is fabricated to fill a gap." },
  { n: "03", label: "Strengthen", icon: Layers,
    body: "Unsupported, ambiguous or overstated claims are surfaced. Revised wording is suggested with the reason and citations — never applied silently. The company decides what to approve." },
  { n: "04", label: "Present", icon: Presentation,
    body: "Produce an Investor Evidence Pack: source-linked, versioned and ready for diligence — with the limitations and generation date on the face of every export. A framework crosswalk view is planned (WP-C) but not yet part of the release." },
];

const PACK_SIGNALS = [
  { icon: CheckCircle2, tone: "supported", label: "Supported claims",
    body: "Claims backed by evidence that meets the KPI-dictionary requirements (definition, baseline, boundary, methodology, reporting period)." },
  { icon: AlertOctagon, tone: "partial", label: "Partially supported claims",
    body: "Claims with evidence present but missing one or more of the components required to confirm — baseline, methodology, boundary or reporting period." },
  { icon: XCircle, tone: "unsupported", label: "Unsupported claims",
    body: "Claims for which no corroborating evidence is present in the uploaded documents. Flagged as release blockers until evidence is added or the claim is revised." },
  { icon: GitCompareArrows, tone: "conflict", label: "Conflicting evidence",
    body: "Two or more sources state materially incompatible values for the same claim. Surfaced side-by-side with jump-to-source links; must be resolved before release." },
  { icon: HelpCircle, tone: "insufficient", label: "Insufficient evidence",
    body: "The evidence exists but its provenance, freshness or specificity is not sufficient for investor-grade diligence. Prioritised as a readiness action." },
  { icon: BookOpen, tone: "framework", label: "Framework references (Planned · WP-C)",
    body: "A dedicated framework crosswalk view against ESRS, IFRS S1/S2, EU Taxonomy and sector frameworks is planned. Today, framework references are surfaced per-claim as evidence organisation only — never as certification, verification, assurance or compliance." },
  { icon: Layers, tone: "gap", label: "Evidence gaps",
    body: "Missing baselines, missing methodologies, missing boundaries, missing reporting periods, unresolved questions — enumerated with the document type most likely to close each gap." },
  { icon: ClipboardCheck, tone: "readiness", label: "Investor-readiness gaps",
    body: "The questions an investor is most likely to ask that the current pack cannot yet answer — prioritised by urgency, with effort estimates." },
  { icon: Presentation, tone: "strengthen", label: "What to strengthen before presenting",
    body: "A concrete, source-linked action list — never a fabricated recommendation. Cosmetic strengthening of weak claims is explicitly prevented." },
];

export default function CompaniesPathway() {
  useEffect(() => {
    document.title = "ImpactVest for Companies | Build investor-ready evidence";
    setPathway("company");
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  return (
    <div className="relative" data-testid={TESTIDS.pathway.companyPage}>
      <Header />
      <main>
        <PathwayHero />
        <Journey />
        <EvidencePack />
        <IntegrityGuardrails />
        <CtaBand />
      </main>
      <Footer />
    </div>
  );
}

function PathwayHero() {
  return (
    <section className="relative pt-28 md:pt-36 pb-20 md:pb-24 overflow-hidden iv-anchor" id="top">
      <div className="grain" aria-hidden />
      <div className="iv-container relative">
        <div className="grid grid-cols-12 gap-8 md:gap-12 items-start">
          <div className="col-span-12 lg:col-span-7">
            <Link to="/" className="font-mono text-[10.5px] tracking-[0.22em] uppercase text-iv-fg-3 hover:text-iv-fg mb-6 inline-flex items-center gap-2">
              <span aria-hidden>←</span> ImpactVest
            </Link>
            <div className="iv-eyebrow mb-8 flex items-center gap-3">
              <Building2 size={14} strokeWidth={1.7} />
              For companies · startups · funds · project sponsors
            </div>
            <h1 className="font-serif-display text-[9vw] sm:text-[7vw] md:text-[4.6vw] lg:text-[3.6rem] xl:text-[4rem] leading-[1.02] tracking-[-0.02em] text-iv-fg">
              Turn your impact and ESG<br className="hidden md:block" /> claims into investor-ready evidence.
            </h1>
            <p className="mt-7 text-[17px] md:text-[18px] leading-relaxed text-iv-fg-2 max-w-xl">
              ImpactVest helps companies organise the evidence behind their sustainability and impact
              claims, identify gaps before diligence, and prepare a credible Investor Evidence Pack —
              without fabricating anything and without cosmetically strengthening weak claims.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-3">
              <Link
                to={withPathway("/sample", "company")}
                data-testid={TESTIDS.pathway.companyPrimaryCta}
                className="iv-btn iv-btn--primary"
              >
                Test your evidence readiness <ArrowRight size={16} />
              </Link>
              <Link
                to={withPathway("/access", "company")}
                data-testid={TESTIDS.pathway.companySecondaryCta}
                className="iv-btn"
              >
                Prepare for investor diligence <ArrowUpRight size={16} />
              </Link>
            </div>
            <p className="mt-7 font-mono text-[11px] tracking-[0.14em] uppercase text-iv-fg-3">
              Signature output · Investor Evidence Pack · Source-linked · Never marketed as compliance
            </p>
          </div>

          <aside className="col-span-12 lg:col-span-5">
            <div className="iv-canvas rounded-[3px]">
              <div className="px-6 pt-5 pb-4 border-b border-[var(--ivc-line)] flex items-center justify-between">
                <span className="font-mono text-[11px] tracking-[0.28em] uppercase text-[var(--ivc-gold-2)]">
                  Investor Evidence Pack
                </span>
                <span className="font-mono text-[10.5px] tracking-[0.22em] uppercase text-[var(--ivc-ivory-3)]">
                  Illustrative
                </span>
              </div>
              <div className="px-7 pt-7 pb-5">
                <div className="text-[11px] font-mono tracking-[0.22em] uppercase text-[var(--ivc-ivory-3)]">Company</div>
                <div className="font-serif-display text-[24px] leading-tight mt-2 text-[var(--ivc-ivory)]">
                  Terravise Nutrition Ltd
                </div>
                <div className="mt-1.5 font-mono text-[10.5px] tracking-[0.22em] uppercase text-[var(--ivc-ivory-3)]">
                  Series A · Food-tech · EU
                </div>
              </div>
              <div className="px-7 pb-6 border-t border-[var(--ivc-line-strong)] pt-5 space-y-3">
                <MiniStat tone="unsupported" label='"100k households reached"' status="No supporting evidence" />
                <MiniStat tone="conflict" label='"30% carbon reduction"' status="Conflicts with LCA report" />
                <MiniStat tone="partial" label='"15–20% farmer income uplift"' status="Missing baseline and methodology" />
              </div>
              <div className="px-7 pb-6 pt-2">
                <div className="text-[10.5px] font-mono tracking-[0.22em] uppercase text-[var(--ivc-ivory-3)]">
                  3 release blockers · 6 readiness actions
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}

function MiniStat({ tone, label, status }) {
  const dot =
    tone === "unsupported" ? "iv-ev-dot--review" :
    tone === "conflict"    ? "iv-ev-dot--review" :
                             "iv-ev-dot--verified";
  return (
    <div className="flex items-start gap-3">
      <span className={`iv-ev-dot ${dot} mt-1.5`} />
      <div className="flex-1">
        <div className="text-[13.5px] leading-snug text-[var(--ivc-ivory)]">{label}</div>
        <div className="text-[11px] font-mono tracking-[0.14em] uppercase text-[var(--ivc-ivory-3)] mt-0.5">{status}</div>
      </div>
    </div>
  );
}

function Journey() {
  return (
    <section className="relative py-24 md:py-28 border-t border-iv-line">
      <div className="iv-container">
        <div className="iv-eyebrow mb-10 md:mb-14">Submit → Substantiate → Strengthen → Present</div>
        <div className="border-t border-iv-line">
          {JOURNEY.map((s, i) => {
            const Icon = s.icon;
            return (
              <Reveal
                key={s.n}
                delay={i * 0.05}
                className="grid grid-cols-12 gap-4 md:gap-10 py-8 md:py-10 border-b border-iv-line items-baseline"
              >
                <div className="col-span-12 md:col-span-2 iv-chapter-number !text-[4rem] md:!text-[5rem]">{s.n}</div>
                <h3 className="col-span-12 md:col-span-3 font-serif-display text-[28px] md:text-[36px] leading-tight text-iv-fg flex items-center gap-3">
                  <span className="text-iv-gold"><Icon size={22} strokeWidth={1.5} /></span>
                  {s.label}
                </h3>
                <p className="col-span-12 md:col-span-7 text-[15px] leading-relaxed text-iv-fg-2">{s.body}</p>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function EvidencePack() {
  return (
    <section className="iv-band-immersive relative py-28 md:py-36 overflow-hidden">
      <div className="iv-container relative">
        <div className="iv-canvas-eyebrow mb-10 md:mb-14">Signature output — Investor Evidence Pack</div>
        <div className="grid grid-cols-12 gap-8 md:gap-12 items-start">
          <div className="col-span-12 lg:col-span-5">
            <h2 className="font-serif-display text-[38px] md:text-[54px] leading-[1.02] tracking-tight text-[var(--ivc-ivory)]">
              What an investor actually needs — organised.
            </h2>
            <p className="mt-8 text-[16px] leading-relaxed text-[var(--ivc-ivory-2)] max-w-lg">
              An Investor Evidence Pack presents every material claim alongside the evidence that
              supports it, the evidence that is missing, and the questions an investor is most likely
              to ask. It is designed for diligence — not for marketing.
            </p>
            <p className="mt-6 text-[14px] leading-relaxed text-[var(--ivc-ivory-3)] max-w-lg">
              ImpactVest emits no readiness score. Freshness and evidence strength are surfaced per-claim only.
              Framework references, where present, describe which disclosure framework the underlying evidence
              would organise against — never certification, assurance, verification or compliance. A dedicated
              framework crosswalk view is planned (WP-C) but is not yet part of the release.
            </p>
          </div>
          <div className="col-span-12 lg:col-span-7">
            <div className="border-t border-[var(--ivc-line-strong)]">
              {PACK_SIGNALS.map((s, i) => {
                const Icon = s.icon;
                return (
                  <div
                    key={s.label}
                    data-testid={`pack-signal-${s.tone}`}
                    className="grid grid-cols-[36px_180px_1fr] gap-4 items-start py-4 border-b border-[var(--ivc-line)]"
                  >
                    <span className="text-[var(--ivc-gold-2)] mt-0.5"><Icon size={18} strokeWidth={1.5} /></span>
                    <div>
                      <div className="font-serif-display text-[16.5px] leading-tight text-[var(--ivc-ivory)]">{s.label}</div>
                      <div className="font-mono text-[9.5px] tracking-[0.22em] uppercase text-[var(--ivc-ivory-3)] mt-1">
                        {String(i + 1).padStart(2, "0")}
                      </div>
                    </div>
                    <p className="text-[13.5px] leading-relaxed text-[var(--ivc-ivory-2)]">{s.body}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

const GUARDRAILS = [
  { label: "Nothing fabricated", body: "The engine never invents citations. If evidence is missing, it is surfaced as missing." },
  { label: "No auto-revision", body: "Suggested claim wording is displayed side-by-side with the original and the reason. The company approves or rejects — silent rewrites are prohibited." },
  { label: "No composite score", body: "ImpactVest does not emit a readiness 0-100. Per-claim status is the honest granularity investors expect." },
  { label: "No compliance language", body: 'Framework references (per-claim today, dedicated crosswalk planned as WP-C) are never marketed as "certified", "assured", "verified" or "compliant".' },
  { label: "Contradictions block release", body: "Unresolved material contradictions between pitch materials, financials and impact reports prevent Pack version release." },
  { label: "Source-linked exports", body: "Every export carries provenance chips, limitations and the exact generation date on its face." },
];

function IntegrityGuardrails() {
  return (
    <section className="relative py-24 md:py-28 border-t border-iv-line bg-iv-bg-2">
      <div className="iv-container">
        <div className="grid grid-cols-12 gap-10 md:gap-14 items-start">
          <div className="col-span-12 lg:col-span-4">
            <div className="iv-eyebrow mb-6">Evidence integrity</div>
            <h2 className="font-serif-display text-[34px] md:text-[44px] leading-[1.05] tracking-tight text-iv-fg">
              Investor-grade evidence — without greenwashing.
            </h2>
            <p className="text-[14px] text-iv-fg-2 mt-6 leading-relaxed max-w-sm">
              ImpactVest is designed to strengthen claims that are actually supportable, not to make
              weak claims look stronger. Every guardrail below is enforced in the engine — not left to
              editorial discretion.
            </p>
          </div>
          <div className="col-span-12 lg:col-span-8">
            <ul className="grid grid-cols-1 sm:grid-cols-2 border-t border-iv-line">
              {GUARDRAILS.map((g, i) => (
                <li key={g.label}
                    className={`px-1 py-5 border-b border-iv-line ${i % 2 === 0 ? "sm:border-r sm:pr-8" : "sm:pl-8"}`}>
                  <div className="font-serif-display text-[18px] leading-tight text-iv-fg">{g.label}</div>
                  <p className="text-[13.5px] leading-relaxed text-iv-fg-2 mt-2">{g.body}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

function CtaBand() {
  return (
    <section className="relative py-28 md:py-36 border-t border-iv-line">
      <div className="iv-container text-center">
        <div className="iv-eyebrow mb-8 justify-center flex">Prepare for diligence</div>
        <h2 className="font-serif-display text-[42px] md:text-[64px] leading-[0.98] tracking-tight text-iv-fg">
          Build the evidence pack.<br className="hidden md:block" /> Present with confidence.
        </h2>
        <div className="mt-10 flex flex-col md:flex-row items-center justify-center gap-4">
          <Link
            to={withPathway("/sample", "company")}
            className="iv-btn iv-btn--primary text-[15px] px-8 py-4"
          >
            Test your evidence readiness <ArrowRight size={16} />
          </Link>
          <Link to="/investors" className="iv-btn text-[15px] px-8 py-4">
            Are you an investor? See the Investor pathway
          </Link>
        </div>
      </div>
    </section>
  );
}
